/**
 * Rutas públicas de marca (path-first, sin subdominio).
 *
 * /gym/{slug}           → landing
 * /gym/{slug}/login     → login con marca de la sede/gym
 * /gym/{slug}/register  → registro
 * /gym/{slug}/invite    → registro con código
 *
 * Legacy: /login?org={slug} se redirige a /gym/{slug}/login
 *
 * API: misma política que services/api.js (VITE_API_BASE_URL=/api en prod).
 * Nunca hardcodear localhost — en el browser de prod el fetch fallaba y
 * AuthLayout caía al branding D28D de plataforma (v8.192).
 */

export function slugifyBrandClient(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/**
 * Base API del shell (nunca Food Nest).
 * Vite define VITE_API_URL=/food-api para el frontend Nest embebido; usarlo aquí
 * provoca 404 en /food-api/gyms/public/by-slug/... (Nest no tiene esa ruta).
 */
export function resolvePublicApiBase() {
  try {
    // Preferir el mismo módulo que axios del shell (no lee VITE_API_URL).
    // Import dinámico síncrono no aplica; usamos VITE_API_BASE_URL o /api.
    const envBase = String(
      (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL)
      || '',
    ).trim();
    if (envBase) {
      const norm = envBase.replace(/\/+$/, '');
      return /\/api$/i.test(norm) ? norm : `${norm}/api`;
    }
  } catch { /* noop */ }
  return '/api';
}

export function parseGymPublicPath(pathname) {
  let path = typeof pathname === 'string'
    ? pathname
    : (typeof window !== 'undefined' ? window.location.pathname : '');
  // WhatsApp/email a veces añaden punto o slash de más al final.
  path = String(path || '').replace(/[.,;:!?]+$/g, '').replace(/\/+$/, '') || '/';
  const match = path.match(/^\/gym\/([^/]+)(?:\/(login|register|invite))?$/i);
  if (!match) return null;
  const slug = decodeURIComponent(match[1] || '').trim();
  if (!slug) return null;
  const rawAction = String(match[2] || 'landing').toLowerCase();
  if (rawAction === 'invite') {
    return { slug, action: 'register', entryMode: 'invite' };
  }
  if (rawAction === 'register') {
    return { slug, action: 'register', entryMode: 'direct' };
  }
  if (rawAction === 'login') {
    return { slug, action: 'login', entryMode: 'direct' };
  }
  return { slug, action: 'landing', entryMode: 'direct' };
}

export function readOrgSlugFromQuery(search) {
  try {
    const q = typeof search === 'string'
      ? search
      : (typeof window !== 'undefined' ? window.location.search : '');
    return String(new URLSearchParams(q).get('org') || '').trim();
  } catch {
    return '';
  }
}

/** Slug activo: path /gym/:slug/* gana sobre ?org= */
export function resolvePublicOrgSlug({ pathname, search } = {}) {
  const fromPath = parseGymPublicPath(pathname)?.slug;
  if (fromPath) return fromPath;
  return readOrgSlugFromQuery(search);
}

export function gymPublicLandingPath(slug) {
  const s = String(slug || '').trim();
  return s ? `/gym/${encodeURIComponent(s)}` : '/gym';
}

export function gymPublicLoginPath(slug) {
  const s = String(slug || '').trim();
  return s ? `/gym/${encodeURIComponent(s)}/login` : '/login';
}

export function gymPublicRegisterPath(slug, { invite = false } = {}) {
  const s = String(slug || '').trim();
  if (!s) return invite ? '/login' : '/login';
  return invite
    ? `/gym/${encodeURIComponent(s)}/invite`
    : `/gym/${encodeURIComponent(s)}/register`;
}

/** Normaliza payload público de gym → auth tenant brand. */
export function normalizePublicGymBrand(gym, fallbackSlug = '') {
  if (!gym || typeof gym !== 'object') return null;
  const links = gym.social_links && typeof gym.social_links === 'object' ? gym.social_links : {};
  const hero = links.login_hero && typeof links.login_hero === 'object' ? links.login_hero : {};
  return {
    id: gym.id || null,
    brand_name: String(gym.brand_name || gym.nombre || '').trim(),
    brand_slug: String(gym.brand_slug || fallbackSlug || '').trim(),
    logo_url: String(gym.logo_url || '').trim(),
    favicon_url: String(gym.favicon_url || '').trim(),
    welcome_message: String(gym.welcome_message || '').trim(),
    primary_color: gym.primary_color || '',
    secondary_color: gym.secondary_color || '',
    support_whatsapp: String(gym.support_whatsapp || '').trim(),
    invite_code: gym.invite_code || null,
    social_links: links,
    login_line_white: String(gym.login_line_white || hero.line_white || '').trim(),
    login_line_yellow_1: String(gym.login_line_yellow_1 || hero.line_yellow_1 || '').trim(),
    login_line_yellow_2: String(gym.login_line_yellow_2 || hero.line_yellow_2 || '').trim(),
  };
}

export async function fetchPublicGymBrand(slug) {
  const clean = String(slug || '').trim();
  if (!clean) return null;
  const base = resolvePublicApiBase().replace(/\/+$/, '');
  const res = await fetch(`${base}/gyms/public/by-slug/${encodeURIComponent(clean)}`);
  if (!res.ok) {
    const err = new Error('gym_not_found');
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  return normalizePublicGymBrand(data?.gym, clean);
}

/** Payload público de coach (login Training / registro por código). */
export function normalizePublicTrainerBrand(trainer, fallbackSlug = '') {
  if (!trainer || typeof trainer !== 'object') return null;
  const links = trainer.social_links && typeof trainer.social_links === 'object' ? trainer.social_links : {};
  const hero = links.login_hero && typeof links.login_hero === 'object' ? links.login_hero : {};
  const slug = String(trainer.brand_slug || fallbackSlug || '').trim();
  return {
    id: trainer.id || null,
    brand_name: String(trainer.brand_name || trainer.nombre || '').trim(),
    brand_slug: slug,
    logo_url: String(trainer.logo_url || '').trim(),
    favicon_url: String(trainer.favicon_url || '').trim(),
    welcome_message: String(trainer.welcome_message || trainer.login_subtitle || '').trim(),
    primary_color: trainer.primary_color || '',
    secondary_color: trainer.secondary_color || '',
    invite_code: trainer.invite_code || null,
    social_links: {
      ...links,
      login_hero: {
        line_white: String(trainer.login_line_white || hero.line_white || '').trim(),
        line_yellow_1: String(trainer.login_line_yellow_1 || hero.line_yellow_1 || '').trim(),
        line_yellow_2: String(trainer.login_line_yellow_2 || hero.line_yellow_2 || '').trim(),
        subtitle: String(trainer.login_subtitle || hero.subtitle || '').trim(),
      },
    },
    login_line_white: String(trainer.login_line_white || hero.line_white || '').trim(),
    login_line_yellow_1: String(trainer.login_line_yellow_1 || hero.line_yellow_1 || '').trim(),
    login_line_yellow_2: String(trainer.login_line_yellow_2 || hero.line_yellow_2 || '').trim(),
  };
}

export async function fetchPublicTrainerBrand(slug) {
  const clean = String(slug || '').trim();
  if (!clean) return null;
  const base = resolvePublicApiBase().replace(/\/+$/, '');
  const res = await fetch(`${base}/trainers/public/by-slug/${encodeURIComponent(clean)}`);
  if (!res.ok) {
    const err = new Error('trainer_not_found');
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  return normalizePublicTrainerBrand(data?.trainer, clean);
}
